import { Button } from '@/components/ui/button';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  useFilesUploadService,
  useKnowledgeGroupsService,
} from '@/services/sources';
import { SearchBar } from '@/shared/SearchBar';
import { COLORS, REGION_DISPLAY_NAMES, SupportedRegion } from '@ditto/shared';
import { Upload } from 'lucide-react';
import { useMemo, useRef, type FC } from 'react';
import { toast } from 'sonner';

const COLOR_MAP: Record<(typeof COLORS)[number], string> = {
  yellow: '#ffed7e',
  blue: '#B0D4FF',
  magenta: '#F3C0F5',
  green: '#A1FAA4',
  purple: '#B2B6FF',
  orange: '#FFCB87',
  red: '#fecaca',
  tan: '#F9FAF5',
};

export interface SourcesFiltersProps {
  searchString: string;
  setSearchString: (value: string) => void;
  selectedKnowledgeGroups: string[];
  setSelectedKnowledgeGroups: (groups: string[]) => void;
  onUploadComplete?: (uploadedFileIds: string[]) => Promise<void>;
  selectedRegions: SupportedRegion[];
  setSelectedRegions: (regions: SupportedRegion[]) => void;
  selectedSourceTypes: string[];
  setSelectedSourceTypes: (sourceTypes: string[]) => void;
}

export const SourcesFilters: FC<SourcesFiltersProps> = ({
  searchString,
  setSearchString,
  selectedKnowledgeGroups,
  setSelectedKnowledgeGroups,
  onUploadComplete,
  selectedRegions,
  setSelectedRegions,
  selectedSourceTypes,
  setSelectedSourceTypes,
}) => {
  const { knowledgeGroups, isLoading: groupsLoading } =
    useKnowledgeGroupsService();
  const { uploadFiles, isUploading } = useFilesUploadService();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const groupOptions: FilterOption[] = useMemo(() => {
    return (
      knowledgeGroups?.map(
        (group: {
          name: string;
          id: string;
          color: (typeof COLORS)[number];
        }) => ({
          label: group.name,
          value: group.id,
          color: COLOR_MAP[group.color],
        })
      ) || []
    );
  }, [knowledgeGroups]);

  const regionOptions: FilterOption[] = [
    { value: 'US', label: `${REGION_DISPLAY_NAMES.US}` },
    { value: 'EU', label: `${REGION_DISPLAY_NAMES.EU}` },
    { value: 'WORLD', label: 'Other' },
  ];

  const sourceTypeOptions: FilterOption[] = [
    { value: 'UserFile', label: 'Uploaded' },
    { value: 'SharedKnowledgeFile', label: 'Provided by Qalico' },
    { value: 'all', label: 'All' },
  ];

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const toastId = toast.loading(`Uploading ${files.length} document(s)…`, {
      position: 'bottom-right',
    });

    try {
      const { prismaFiles } = await uploadFiles(files, 'shown');

      toast.message(`Analyzing ${files.length} document(s)…`, {
        id: toastId,
      });

      await onUploadComplete?.(prismaFiles.map(f => f.id));

      toast.success(
        `Successfully added ${files.length} document(s) to knowledge base`,
        {
          id: toastId,
        }
      );
    } catch (error) {
      toast.error(
        `Error adding ${files.length} document(s) to knowledge base`,
        { id: toastId }
      );
    } finally {
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <FlexCol className="gap-6">
      <FlexRow gap="4" alignItems="center" justifyContent="between">
        <SearchBar
          searchString={searchString}
          setSearchString={setSearchString}
          placeholder="Search documents..."
          className="w-[350px]"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.txt"
        />
        <Button
          variant="dark"
          onClick={handleUploadClick}
          disabled={isUploading}
          loading={isUploading}
        >
          <Upload size={16} />
          Upload document
        </Button>
      </FlexRow>
      <FlexRow gap="2" className="flex-wrap">
        <OptionFilter
          label="Knowledge group"
          options={groupOptions}
          selected={selectedKnowledgeGroups}
          onChange={setSelectedKnowledgeGroups}
          disabled={groupsLoading}
        />
        <OptionFilter
          label="Regulatory region"
          options={regionOptions}
          selected={selectedRegions}
          onChange={values => setSelectedRegions(values as SupportedRegion[])}
        />
        <OptionFilter
          label="Source"
          options={sourceTypeOptions}
          selected={selectedSourceTypes}
          onChange={setSelectedSourceTypes}
        />
      </FlexRow>
    </FlexCol>
  );
};

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;
  const activeLabel =
    selected.length === 1
      ? options.find(o => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={isActive ? activeLabel : undefined}
      onClear={isActive ? () => onChange([]) : undefined}
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};
